import React from "react";
import {ButtonBase} from "@mui/material";
import {ChevronRight, ScheduleOutlined} from "@mui/icons-material";
import {useLocation, useNavigate} from "react-router";
import type {ComputedOpportunity} from "../briefingLogic";
import {confidenceDisplay, confidenceState, costOfWaitingTag} from "../briefingLogic";
import {useBriefing} from "../briefingState";
import {OpportunityTypeIcon} from "../opportunityTypeIcon.tsx";
import {TimingPanel} from "./TimingPanel";
import {ConfidencePopover} from "./ConfidencePopover";

type Props = {
    computed: ComputedOpportunity
};

/**
 * One opportunity card — a decision card, not the whole framework. One fact per
 * zone, and no token repeated: the type lives in the eyebrow, the action in the
 * headline, the population in the applies-to line (curated to exclude whatever
 * the headline already says), the numbers in the cost-of-waiting box.
 */
export const OpportunityCard: React.FC<Props> = ({computed}) => {
    const {state} = useBriefing();
    const navigate = useNavigate();
    const location = useLocation();
    const opportunity = computed.opportunity;

    const [timingAnchor, setTimingAnchor] = React.useState<HTMLElement | null>(null);
    const [confidenceAnchor, setConfidenceAnchor] = React.useState<HTMLElement | null>(null);

    const highlighted = state.highlightOpportunityId === opportunity.id;
    const openOpportunity = () => navigate(`${location.pathname}/opportunity/${opportunity.id}`);

    const stop = (e: React.SyntheticEvent) => e.stopPropagation();

    return (
        <article
            className={"db-card" + (highlighted ? " db-card-highlight" : "")}
            role="button"
            tabIndex={0}
            aria-label={opportunity.title}
            onClick={openOpportunity}
            onKeyDown={e => {
                if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    openOpportunity();
                }
            }}
        >
            <header className="db-card-eyebrow">
                <span className="db-card-typemark">
                    <OpportunityTypeIcon moveType={opportunity.opportunity_type}/>
                </span>
                <span className="db-card-type">{opportunity.opportunity_type_label}</span>
                <ButtonBase
                    className="db-card-confidence"
                    aria-label="Confidence breakdown"
                    onClick={e => {
                        stop(e);
                        setConfidenceAnchor(e.currentTarget);
                    }}
                >
                    {confidenceDisplay(computed)} · {confidenceState(computed.confidence)}
                </ButtonBase>
                <ChevronRight className="db-card-chevron"/>
            </header>

            <p className="db-card-headline">{opportunity.title}</p>
            <p className="db-card-applies">{computed.appliesTo}</p>
            <p className="db-card-plain">{computed.costPlainLanguage}</p>

            <div className="db-cow-box">
                <div className="db-cow-head">
                    <span className="db-cow-label">Cost of waiting</span>
                    <span className="db-cow-value">
                        {computed.costDisplay}
                        <em className="db-cow-tag"> · {costOfWaitingTag(opportunity)}</em>
                    </span>
                </div>
                <div className="db-cow-facts">
                    {computed.facts.map(f =>
                        f.type === 'timing'
                            ? <ButtonBase
                                key={f.slot}
                                className="db-cow-fact db-cow-fact-clickable"
                                aria-label="Timing"
                                onClick={e => {
                                    stop(e);
                                    setTimingAnchor(e.currentTarget);
                                }}
                            >
                                <span className="db-cow-fact-label">{f.label}:</span> <b>{f.display}</b>
                            </ButtonBase>
                            : <span key={f.slot} className="db-cow-fact">
                                <span className="db-cow-fact-label">{f.label}:</span> <b>{f.display}</b>
                            </span>
                    )}
                </div>
            </div>

            <footer className="db-card-foot">
                <ButtonBase
                    className="db-card-clocklabel"
                    aria-label="Timing derivation"
                    onClick={e => {
                        stop(e);
                        setTimingAnchor(e.currentTarget);
                    }}
                >
                    <ScheduleOutlined sx={{fontSize: 14}}/> {computed.timingLabel}
                </ButtonBase>
                <span className={`db-card-pill db-tone-${opportunity.status_pill.tone}`}>
                    {opportunity.status_pill.label}
                </span>
            </footer>

            <span onClick={stop}>
                <TimingPanel computed={computed} anchorEl={timingAnchor} onClose={() => setTimingAnchor(null)}/>
                <ConfidencePopover computed={computed} anchorEl={confidenceAnchor} onClose={() => setConfidenceAnchor(null)}/>
            </span>
        </article>
    );
};
